import { API, BACKEND_URL } from '@/config/api';
import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { nb } from 'date-fns/locale';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Layers, Save, Calendar as CalendarIcon, Sparkles, Trash2, Pencil, MoreVertical, Copy, MapPin, Settings2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const SampleFormHeader = ({
  selectedTemplate,
  setSelectedTemplate,
  availableTemplates,
  setAvailableTemplates,
  sampleDate,
  setSampleDate,
  selectedArea,
  setSelectedArea,
  areas,
  handleSubmitAll,
  loading,
  generatingSummary,
  getCompletionPercentage,
  hasUnsavedData = false,
  onSaveDraft,
  draftSaved,
  onSaveAsDraft,
  onEditSections
}) => {
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [renameTemplateId, setRenameTemplateId] = useState('');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTemplateId, setDeleteTemplateId] = useState('');
  const [deleteTemplateName, setDeleteTemplateName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [pendingTemplateChange, setPendingTemplateChange] = useState(null);
  const [showCopyToAreaDialog, setShowCopyToAreaDialog] = useState(false);
  const [copyTargetAreaId, setCopyTargetAreaId] = useState('');
  const [copyNewName, setCopyNewName] = useState('');
  const [copying, setCopying] = useState(false);
  const [areaLocations, setAreaLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);

  const currentTemplate = availableTemplates.find(t => t.id === selectedTemplate);

  useEffect(() => {
    const fetchAreaLocations = async () => {
      if (!copyTargetAreaId) { setAreaLocations([]); return; }
      setLoadingLocations(true);
      try {
        const response = await axios.get(`${API}/locations`, { withCredentials: true });
        setAreaLocations(response.data.filter(loc => loc.area_id === copyTargetAreaId));
      } catch { setAreaLocations([]); }
      finally { setLoadingLocations(false); }
    };
    fetchAreaLocations();
  }, [copyTargetAreaId]);

  const handleOpenRenameDialog = () => {
    if (!currentTemplate) return;
    setRenameTemplateId(currentTemplate.id);
    setNewTemplateName(currentTemplate.name);
    setShowRenameDialog(true);
  };

  const handleRenameTemplate = async () => {
    if (!newTemplateName.trim()) { toast.error('Namn kan ikkje vere tomt'); return; }
    setRenaming(true);
    try {
      await axios.put(`${API}/bulk-templates/${renameTemplateId}`, { name: newTemplateName.trim() }, { withCredentials: true });
      if (setAvailableTemplates) {
        setAvailableTemplates(prev => prev.map(t => t.id === renameTemplateId ? { ...t, name: newTemplateName.trim() } : t));
      }
      toast.success('Skjema omdøpt!');
      setShowRenameDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikkje endre namn');
    } finally { setRenaming(false); }
  };

  const handleTemplateChange = (newTemplateId) => {
    if (hasUnsavedData && !draftSaved) {
      setPendingTemplateChange(newTemplateId);
      setShowUnsavedWarning(true);
    } else {
      setSelectedTemplate(newTemplateId);
    }
  };

  const handleConfirmTemplateChange = async (saveFirst) => {
    if (saveFirst && onSaveDraft) { await onSaveDraft(); toast.success('Utkast lagra!'); }
    if (pendingTemplateChange) setSelectedTemplate(pendingTemplateChange);
    setShowUnsavedWarning(false);
    setPendingTemplateChange(null);
  };

  const handleOpenDeleteDialog = () => {
    if (!currentTemplate) return;
    setDeleteTemplateId(currentTemplate.id);
    setDeleteTemplateName(currentTemplate.name);
    setShowDeleteDialog(true);
  };

  const handleDeleteTemplate = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/bulk-templates/${deleteTemplateId}`, { withCredentials: true });
      if (setAvailableTemplates) setAvailableTemplates(prev => prev.filter(t => t.id !== deleteTemplateId));
      if (selectedTemplate === deleteTemplateId) {
        const remaining = availableTemplates.filter(t => t.id !== deleteTemplateId);
        setSelectedTemplate(remaining.length > 0 ? remaining[0].id : '');
      }
      toast.success('Skjema sletta!');
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikkje slette skjema');
    } finally { setDeleting(false); }
  };

  const handleOpenCopyDialog = () => {
    if (!currentTemplate) return;
    setCopyTargetAreaId(''); setCopyNewName(''); setAreaLocations([]);
    setShowCopyToAreaDialog(true);
  };

  const handleCopyToArea = async () => {
    if (!copyTargetAreaId) { toast.error('Vel eit område'); return; }
    if (areaLocations.length === 0) { toast.error('Ingen lokalitetar funne'); return; }
    const targetArea = areas.find(a => a.id === copyTargetAreaId);
    const finalName = copyNewName.trim() || `${targetArea?.name || copyTargetAreaId}`;
    setCopying(true);
    try {
      const newLocations = areaLocations.map(loc => ({
        id: loc.id || `loc-${loc.letter?.toLowerCase()}`,
        letter: loc.letter, name: loc.name, full_name: `${loc.letter} - ${loc.name}`
      }));
      await axios.post(`${API}/bulk-templates`, {
        name: finalName, description: `Kopiert frå ${currentTemplate.name}`, is_default: false,
        config: { locations: newLocations, organisms: currentTemplate.config?.organisms || [], zooplankton: currentTemplate.config?.zooplankton || [] }
      }, { withCredentials: true });
      const templatesResponse = await axios.get(`${API}/bulk-templates`, { withCredentials: true });
      if (setAvailableTemplates) setAvailableTemplates(templatesResponse.data);
      const newTemplateData = templatesResponse.data.find(t => t.name === finalName);
      if (newTemplateData) setSelectedTemplate(newTemplateData.id);
      toast.success(`Nytt skjema "${finalName}" oppretta!`);
      setShowCopyToAreaDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikkje kopiere skjema');
    } finally { setCopying(false); }
  };

  const pct = getCompletionPercentage();

  return (
    <>
      {/* Clean card-based form */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm mb-5" data-testid="sample-form-header">
        <div className="flex items-center gap-3 px-4 py-3 flex-wrap">
          {/* Schema */}
          <div className="flex items-center gap-1.5" data-tour="template-selector">
            <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
              <SelectTrigger className="h-9 border border-slate-200 rounded-lg text-sm w-[230px]" data-testid="schema-selector">
                <SelectValue placeholder="Vel skjema" />
              </SelectTrigger>
              <SelectContent>
                {availableTemplates.map((tpl) => (
                  <SelectItem key={tpl.id} value={tpl.id}>
                    <div className="font-medium text-slate-900 flex items-center gap-1.5">
                      <span className="text-sm">{tpl.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTemplate && currentTemplate && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9 w-9 p-0 border-slate-200 shrink-0" data-testid="schema-options-btn">
                    <MoreVertical className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <DropdownMenuItem onClick={handleOpenRenameDialog} className="cursor-pointer" data-testid="rename-schema-btn">
                    <Pencil className="w-4 h-4 mr-2 text-blue-600" /> Endre namn
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleOpenCopyDialog} className="cursor-pointer" data-testid="copy-to-area-btn">
                    <Copy className="w-4 h-4 mr-2 text-teal-600" /> Kopier til nytt område
                  </DropdownMenuItem>
                  {onEditSections && (
                    <DropdownMenuItem onClick={onEditSections} className="cursor-pointer" data-testid="edit-sections-btn">
                      <Settings2 className="w-4 h-4 mr-2 text-purple-600" /> Rediger seksjonar
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleOpenDeleteDialog} className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50" data-testid="delete-schema-btn">
                    <Trash2 className="w-4 h-4 mr-2" /> Slett skjema
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          <div className="w-px h-6 bg-slate-200 hidden md:block" />

          {/* Date */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-9 w-[155px] justify-start text-left font-normal border border-slate-200 rounded-lg text-sm" data-testid="date-picker" data-tour="area-date-selector">
                <CalendarIcon className="mr-2 h-3.5 w-3.5 text-slate-400" />
                {sampleDate ? format(sampleDate, "d. MMM yyyy", { locale: nb }) : <span className="text-slate-400">Dato</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={sampleDate} onSelect={setSampleDate} initialFocus />
            </PopoverContent>
          </Popover>

          <div className="w-px h-6 bg-slate-200 hidden md:block" />

          {/* Area */}
          <Select value={selectedArea} onValueChange={setSelectedArea}>
            <SelectTrigger className="h-9 border border-slate-200 rounded-lg text-sm w-[155px]" data-testid="area-selector">
              <SelectValue placeholder="Vel område" />
            </SelectTrigger>
            <SelectContent>
              {areas.map((area) => (
                <SelectItem key={area.id} value={area.id}>
                  <span className="text-sm font-medium">{area.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex-1" />

          {/* Save button — explicit save (same as auto-save, for user confidence) */}
          {onSaveDraft && selectedArea && (
            <Button
              onClick={async () => {
                try {
                  await onSaveDraft();
                  toast.success('Lagra!');
                } catch (e) {
                  toast.error('Kunne ikkje lagre');
                }
              }}
              disabled={loading || !selectedArea}
              variant="outline"
              className="h-9 px-4 rounded-lg font-medium text-sm shrink-0"
              data-testid="save-draft-btn"
            >
              <Save className="w-3.5 h-3.5 mr-1.5" />
              Lagre utkast
            </Button>
          )}

          {/* Generate */}
          <Button
            onClick={handleSubmitAll}
            disabled={loading || !selectedArea || generatingSummary || pct < 100}
            className="h-9 px-4 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium text-sm shadow-sm shrink-0"
            data-testid="generate-summary-btn"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            {generatingSummary ? 'Genererer...' : !selectedArea ? 'Vel område' : pct < 100 ? `${pct}%` : 'Lag rapport'}
          </Button>
        </div>
      </div>

      {/* Rename Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-blue-600" /> Endre namn på skjema</DialogTitle>
            <DialogDescription>Skriv inn nytt namn for skjemaet</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input value={newTemplateName} onChange={(e) => setNewTemplateName(e.target.value)} placeholder="Nytt namn..." className="h-11" data-testid="rename-input"
              onKeyDown={(e) => { if (e.key === 'Enter') handleRenameTemplate(); }} />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRenameDialog(false)}>Avbryt</Button>
            <Button onClick={handleRenameTemplate} disabled={renaming || !newTemplateName.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-rename-btn">
              {renaming ? 'Lagrar...' : 'Lagre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600"><Trash2 className="w-5 h-5" /> Slett skjema</DialogTitle>
            <DialogDescription>Er du sikker? Denne handlinga kan ikkje angrast.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              Du er i ferd med å slette: <strong>{deleteTemplateName}</strong>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Avbryt</Button>
            <Button onClick={handleDeleteTemplate} disabled={deleting} variant="destructive" data-testid="confirm-delete-btn">
              {deleting ? 'Slettar...' : 'Ja, slett'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy to Area Dialog */}
      <Dialog open={showCopyToAreaDialog} onOpenChange={setShowCopyToAreaDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Copy className="w-5 h-5 text-teal-600" /> Kopier skjema til nytt område</DialogTitle>
            <DialogDescription>Kopierer organismar frå "{currentTemplate?.name}" til eit nytt område</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">
              <strong>Kjelde:</strong> {currentTemplate?.name} — {currentTemplate?.config?.organisms?.length || 0} algar, {currentTemplate?.config?.zooplankton?.length || 0} zooplankton
            </div>
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Målområde</label>
              <Select value={copyTargetAreaId} onValueChange={setCopyTargetAreaId}>
                <SelectTrigger className="h-10"><SelectValue placeholder="Vel område..." /></SelectTrigger>
                <SelectContent>
                  {areas.map((area) => (<SelectItem key={area.id} value={area.id}>{area.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {copyTargetAreaId && (
              <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <div className="flex items-center gap-1.5 mb-2 text-sm font-semibold text-teal-800">
                  <MapPin className="w-3.5 h-3.5" /> Lokalitetar:
                </div>
                {loadingLocations ? <p className="text-sm text-teal-600">Lastar...</p> :
                  areaLocations.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {areaLocations.map((loc) => (
                        <span key={loc.id || loc.letter} className="px-2 py-0.5 bg-white rounded text-xs font-medium text-teal-700 border border-teal-200">
                          {loc.letter} - {loc.name}
                        </span>
                      ))}
                    </div>
                  ) : <p className="text-sm text-orange-600">Ingen lokalitetar funne</p>}
              </div>
            )}
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Namn (valfritt)</label>
              <Input value={copyNewName} onChange={(e) => setCopyNewName(e.target.value)}
                placeholder={areas.find(a => a.id === copyTargetAreaId)?.name || 'Nytt skjema'} className="h-10" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCopyToAreaDialog(false)}>Avbryt</Button>
            <Button onClick={handleCopyToArea} disabled={copying || !copyTargetAreaId || areaLocations.length === 0}
              className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-copy-btn">
              {copying ? 'Kopierer...' : 'Opprett skjema'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Warning */}
      <Dialog open={showUnsavedWarning} onOpenChange={setShowUnsavedWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600"><Save className="w-5 h-5" /> Ulagra data</DialogTitle>
            <DialogDescription>Du har ulagra data. Kva vil du gjere?</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Dersom du byter skjema utan å lagre, vert data du har lagt inn tapt.
            </div>
          </div>
          <DialogFooter className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setShowUnsavedWarning(false); setPendingTemplateChange(null); }}>Avbryt</Button>
            <Button onClick={() => handleConfirmTemplateChange(false)} variant="destructive">Forkast og byt</Button>
            <Button onClick={() => handleConfirmTemplateChange(true)} className="bg-teal-600 hover:bg-teal-700">
              <Save className="w-4 h-4 mr-2" /> Lagre og byt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SampleFormHeader;
